import { useMutation, useQueryClient } from '@tanstack/react-query'
import { addCancellationPolicy } from 'api/company/addCancellationPolicy'
import { companyDetailKey } from './keys'
import { CompanyDetail } from 'api/company/companyDetail'
import { addMinutes } from 'date-fns'

export const useAddCancellationPolicyMutation = (companyId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: Parameters<typeof addCancellationPolicy>[0]) =>
      addCancellationPolicy(request),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: companyDetailKey(companyId) })

      const previousCompanyDetail = queryClient.getQueryData(
        companyDetailKey(companyId)
      )

      // Optimistically update to the new value
      queryClient.setQueryData<CompanyDetail>(
        companyDetailKey(companyId),
        (old) => {
          if (!old) return old
          const newCompanyDetail = {
            ...old,
          }

          const newUpcomingCancellationPolicy = [
            ...old.upcomingCancellationPolicies,
            {
              id: Math.floor(Math.random() * 1000000),
              isDefault: false,
              startsAt: variables.startsAt
                ? variables.startsAt
                : addMinutes(Date.now(), 1).toISOString(),
              policyWindows: variables.policyWindows.map((window) => ({
                cutoffTimeHours: window.cutoffTime,
                ...(window.chargeMinutes != null
                  ? {
                      chargeMinutes: window.chargeMinutes,
                      chargePercentage: null,
                    }
                  : {
                      chargePercentage: window.chargePercentage,
                      chargeMinutes: null,
                    }),
              })),
            },
          ]

          newCompanyDetail.upcomingCancellationPolicies =
            newUpcomingCancellationPolicy

          return newCompanyDetail
        }
      )

      return { previousCompanyDetail }
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(
        companyDetailKey(companyId),
        context?.previousCompanyDetail
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: companyDetailKey(companyId) })
    },
  })
}
